import {Euler, type Group, type Mesh, Quaternion, Vector3, Vector4} from 'three'

import type {BrandedDividerParams} from '../../../../components/WebGLContext/WebGLContext.types'
import BaseMona from './BaseMona'

export default class Mona0 extends BaseMona {
  constructor(params: BrandedDividerParams, rectData: Vector4) {
    super(params, rectData)

    const scale = 0.16
    this.group.scale.set(scale, scale, scale)
  }

  override createTl(): void {
    this.tl.add(() => {
      this.group.visible = true
    }, 0)

    // Eyes animation
    const eyeGroup = this.group.getObjectByName('EyeGroup') as Group
    const mirroredEyeGroup = this.group.getObjectByName('EyeGroup_mirrored') as Group
    if (eyeGroup && mirroredEyeGroup) {
      const y = -0.4
      this.tl
        .fromTo(
          eyeGroup.position,
          {
            y,
            x: -0.1,
          },
          {
            y: 0,
            x: 0,
            ease: 'expo.out',
            duration: 2,
          },
          1,
        )
        .fromTo(
          mirroredEyeGroup.position,
          {
            y,
            x: 0.1,
          },
          {
            y: 0,
            x: 0,
            ease: 'expo.out',
            duration: 2,
          },
          1,
        )
    }

    // Mouth animation
    const mouth = this.group.getObjectByName('Mouth') as Mesh
    if (mouth) {
      this.tl
        .fromTo(
          mouth.position,
          {
            x: 0,
          },
          {
            x: 0.2,
            ease: 'expo.out',
            duration: 2,
          },
          2,
        )
        .fromTo(
          mouth.rotation,
          {
            z: 0,
          },
          {
            z: 0.2,
            ease: 'expo.out',
            duration: 2,
          },
          2,
        )
        .to(
          mouth.scale,
          {
            x: 0.8,
            y: 0.8,
            z: 0.8,
            ease: 'expo.out',
            duration: 2,
          },
          2,
        )
    }

    const createWhiskerAnimation = (group: Mesh[], sign: number) => {
      group.forEach((mesh, index) => {
        const duration = 0.5
        const step = 0.1
        this.tl
          .to(
            mesh.rotation,
            {
              z: -0.4 * sign,
              duration,
              // repeatDelay: 0.5,
            },
            index * step,
          )
          .to(
            mesh.rotation,
            {
              z: 0.3 * sign,
              duration,
              // repeatDelay: 0.5,
            },
            duration + index * step,
          )
          .to(
            mesh.rotation,
            {
              z: -0.2 * sign,
              duration,
              // repeatDelay: 0.5,
            },
            duration * 2 + index * step,
          )
          .to(
            mesh.rotation,
            {
              z: 0.2 * sign,
              duration,
              // repeatDelay: 0.5,
            },
            duration * 3 + index * step,
          )
          .to(
            mesh.rotation,
            {
              z: 0,
              duration,
              // repeatDelay: 0.5,
            },
            duration * 4 + index * step,
          )
      })
    }

    // Whiskers animation
    createWhiskerAnimation(this.whiskers.original, 1)
    createWhiskerAnimation(this.whiskers.mirrored, -1)

    this.group.rotation.order = 'ZYX'
    this.group.userData.rotation = this.group.rotation.clone()
    this.group.userData.rotation.z = Math.PI * 0.25

    // Group animation
    const startPos = new Vector3(2.6, -2.6, 0)
    this.tl
      .fromTo(
        this.group.userData.rotation,
        {
          x: 2,
          y: Math.PI * 3,
        },
        {
          x: -0.2,
          y: 0,
          duration: 3,
          ease: 'expo.out',
        },
        0,
      )

      .fromTo(
        this.group.position,
        {
          x: startPos.x,
          y: startPos.y,
        },
        {
          x: startPos.x - 2,
          y: startPos.y + 2,
          duration: 3,
          ease: 'expo.out',
        },
        0,
      )

    super.createTl()
  }

  override update(scrollProgress: number) {
    if (!this.group.userData.rotation) return
    const euler = new Euler(-scrollProgress * 0.5, 0, 0)
    const quaternion = new Quaternion().setFromEuler(euler)
    this.group.quaternion.setFromEuler(this.group.userData.rotation)
    this.group.quaternion.multiply(quaternion)
  }
}
